import React from "react";
import { Col, Card } from "antd";

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import InsightsCard from "./insightChart";
import TradesSection from "./tradeChart";
import "../css/strategy.css";
const GOLD = "#d09d4c";
const GRAY = "#808080";
const WHITE = "#ffffff";

/* =================================
   GLASS TOOLTIP
================================= */


const CustomStrategyTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const item = payload[0];
  const value = Number(item.value ?? 0);

  // Win/Loss chart mein category ke basis par heading
  const isWinLossChart = label === "Wins" || label === "Loss";

  const isProfit = isWinLossChart
    ? label === "Wins"
    : value >= 0;

  const heading = isWinLossChart
    ? label === "Wins"
      ? "Profit"
      : "Loss"
    : isProfit
    ? "Profit"
    : "Loss";

  return (
    <div className="strategy-glass-tooltip">
      <div
        className={`strategy-tooltip-heading ${
          isProfit ? "tooltip-profit" : "tooltip-loss"
        }`}
      >
        <span className="strategy-tooltip-dot" />
        {heading}
      </div>

      <div className="strategy-tooltip-label">
        Label: {label ?? "-"}
      </div>

      <div
        className={`strategy-tooltip-value ${
          isProfit ? "tooltip-profit" : "tooltip-loss"
        }`}
      >
        Value:{" "}
        {isWinLossChart
          ? value
          : `₹ ${value.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
      </div>
    </div>
  );
};


/* =================================
   REUSABLE CHART AXIS PROPS
================================= */

const axisProps = {
  tick: {
    fill: WHITE,
    fontSize: 12,
  },
  axisLine: {
    stroke: GRAY,
    strokeOpacity: 0.5,
  },
  tickLine: {
    stroke: GRAY,
    strokeOpacity: 0.5,
  },
};

/* =================================
   DASHBOARD CHARTS
================================= */

const DashboardCharts = ({ data }) => {
  const areaData = data?.equity_curve || [];

  const barData = [
    {
      name: "Wins",
      value: data?.win_loss?.wins?.length || 0,
    },
    {
      name: "Loss",
      value: data?.win_loss?.losses?.length || 0,
    },
  ];

  const lineData = data?.score || [];

  return (
    <>
      {/* Lincfolio Score */}
      <Col xs={24} lg={12}>
        <Card
          title="Lincfolio Score"
          className="strategy-chart-card"
          bordered={false}
        >
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={lineData}
              margin={{ top: 10, right: 12, left: 0, bottom: 4 }}
            >
              <XAxis dataKey="week" {...axisProps} />

              <YAxis {...axisProps} />

              <Tooltip
                content={<CustomStrategyTooltip />}
                cursor={{
                  stroke: GOLD,
                  strokeOpacity: 0.25,
                }}
                wrapperStyle={{ zIndex: 20, outline: "none" }}
              />

              <Line
                type="monotone"
                dataKey="score"
                name="Score"
                stroke={GOLD}
                strokeWidth={3}
                dot={{
                  fill: GOLD,
                  stroke: GOLD,
                  r: 3,
                }}
                activeDot={{
                  r: 6,
                  fill: GOLD,
                  stroke: WHITE,
                  strokeWidth: 1,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      {/* Equity Curve */}
      <Col xs={24} lg={12}>
        <Card
          title="Equity Curve"
          className="strategy-chart-card"
          bordered={false}
        >
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart
              data={areaData}
              margin={{ top: 10, right: 12, left: 0, bottom: 4 }}
            >
              <defs>
                <linearGradient
                  id="strategyEquityGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={GOLD}
                    stopOpacity={0.38}
                  />
                  <stop
                    offset="95%"
                    stopColor={GOLD}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>

              <XAxis dataKey="day" {...axisProps} />

              <YAxis {...axisProps} />

              <Tooltip
                content={<CustomStrategyTooltip />}
                cursor={{
                  stroke: GOLD,
                  strokeOpacity: 0.25,
                }}
                wrapperStyle={{ zIndex: 20, outline: "none" }}
              />

              <Area
                type="monotone"
                dataKey="pnl"
                name="P&L"
                stroke={GOLD}
                strokeWidth={2.5}
                fill="url(#strategyEquityGradient)"
                activeDot={{
                  r: 5,
                  fill: GOLD,
                  stroke: WHITE,
                  strokeWidth: 1,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      {/* Win vs Loss */}
      <Col xs={24} lg={12}>
        <Card
          title="Win vs Loss"
          className="strategy-chart-card"
          bordered={false}
        >
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={barData}
              margin={{ top: 10, right: 12, left: 0, bottom: 4 }}
            >
              <XAxis dataKey="name" {...axisProps} />

              <YAxis
                allowDecimals={false}
                {...axisProps}
              />

              <Tooltip
                content={<CustomStrategyTooltip />}
                cursor={{
                  fill: "rgba(208, 157, 76, 0.06)",
                }}
                wrapperStyle={{ zIndex: 20, outline: "none" }}
              />

              <Bar
                dataKey="value"
                name="Trades"
                fill={GOLD}
                barSize={50}
                radius={[6, 6, 0, 0]}
                background={{ fill: "transparent" }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      {/* Insights */}
      <Col xs={24} lg={12}>
        <InsightsCard strategyId={data?.strategy_id} />
      </Col>

      {/* Trades */}
      <Col xs={24} lg={24}>
        <TradesSection data={data} />
      </Col>
    </>
  );
};

export default DashboardCharts;